import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { CABIN_CLASSES } from '../../../domain/models/contracts';
import type { CabinClass, PerCabin, RouteAnalyticsView } from '../../../domain/models/contracts';
import type { UpdateTicketPriceCommand } from '../../../domain/models/contracts';
import { PriceSensitivityIndicator } from './PriceSensitivityIndicator';
import { assessPriceSensitivity } from './routeMetrics';
import { formatUsdCents } from './formatters';

const CABIN_LABELS: Readonly<Record<CabinClass, string>> = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium Economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

type FareDraft = Record<CabinClass, number>;

const SLIDER_MIN_FACTOR = 0.4;
const SLIDER_MAX_FACTOR = 2.5;

function toDraft(fares: PerCabin<number>): FareDraft {
  return {
    ECONOMY: fares.ECONOMY,
    PREMIUM_ECONOMY: fares.PREMIUM_ECONOMY,
    BUSINESS: fares.BUSINESS,
    FIRST: fares.FIRST,
  };
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

interface FareControlsProps {
  readonly route: RouteAnalyticsView;
  /** Dispatches UPDATE_TICKET_PRICE to the simulation worker. */
  readonly onUpdateTicketPrice: (command: UpdateTicketPriceCommand) => void;
}

export function FareControls({ route, onUpdateTicketPrice }: FareControlsProps) {
  const { legId, currentFares, marketBaselineFares, seatsByCabin } = route;
  const { ECONOMY, PREMIUM_ECONOMY, BUSINESS, FIRST } = currentFares;

  const [draft, setDraft] = useState<FareDraft>(() => toDraft(currentFares));

  // Re-sync the draft when the player switches route or the worker publishes
  // different live fares (e.g. after applying, or yield-management repricing).
  useEffect(() => {
    setDraft({ ECONOMY, PREMIUM_ECONOMY, BUSINESS, FIRST });
  }, [legId, ECONOMY, PREMIUM_ECONOMY, BUSINESS, FIRST]);

  const operatedCabins = useMemo(
    () => CABIN_CLASSES.filter((cabin) => seatsByCabin[cabin] > 0),
    [seatsByCabin],
  );

  const isDirty = operatedCabins.some(
    (cabin) => Math.abs(draft[cabin] - currentFares[cabin]) >= 0.005,
  );
  const isValid = operatedCabins.every(
    (cabin) => Number.isFinite(draft[cabin]) && draft[cabin] > 0,
  );

  const setFare = (cabin: CabinClass, value: number): void => {
    setDraft((previous) => ({ ...previous, [cabin]: value }));
  };

  const handleInput =
    (cabin: CabinClass) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      const parsed = event.target.valueAsNumber;
      if (Number.isFinite(parsed)) {
        setFare(cabin, parsed);
      }
    };

  const handleReset = (): void => {
    setDraft(toDraft(currentFares));
  };

  const handleApply = (): void => {
    if (!isDirty || !isValid) return;
    // Cabins the route does not sell keep their published fare unchanged.
    const fare = (cabin: CabinClass): number =>
      roundCents(seatsByCabin[cabin] > 0 ? draft[cabin] : currentFares[cabin]);

    onUpdateTicketPrice({
      type: 'UPDATE_TICKET_PRICE',
      legId,
      fareEconomy: fare('ECONOMY'),
      fareBusiness: fare('BUSINESS'),
      farePremiumEconomy: fare('PREMIUM_ECONOMY'),
      fareFirst: fare('FIRST'),
    });
  };

  return (
    <section className="ri-panel" aria-label="Ticket pricing">
      <header className="ri-panel__header">
        <h2 className="ri-panel__title">Ticket Pricing</h2>
        <p className="ri-panel__subtitle">
          Adjust fares per cabin. Estimates use this route&apos;s price elasticity.
        </p>
      </header>

      <ul className="ri-fares">
        {CABIN_CLASSES.map((cabin) => {
          const seats = seatsByCabin[cabin];
          const market = marketBaselineFares[cabin];
          const current = currentFares[cabin];
          const disabled = seats <= 0;
          const sliderMin = Math.max(1, Math.floor(market * SLIDER_MIN_FACTOR));
          const sliderMax = Math.ceil(Math.max(market * SLIDER_MAX_FACTOR, current * 1.25));
          const inputId = `ri-fare-${legId}-${cabin}`;
          const sensitivity = assessPriceSensitivity(cabin, draft[cabin], route);
          const changed = Math.abs(draft[cabin] - current) >= 0.005;

          return (
            <li key={cabin} className={`ri-fare${disabled ? ' ri-fare--disabled' : ''}`}>
              <div className="ri-fare__head">
                <label className="ri-fare__name" htmlFor={inputId}>
                  {CABIN_LABELS[cabin]}
                </label>
                <span className="ri-fare__meta">
                  {disabled
                    ? 'Not configured on this route'
                    : `${seats} seats · live ${formatUsdCents(current)} · market ${formatUsdCents(market)}`}
                </span>
              </div>

              <div className="ri-fare__inputs">
                <input
                  className="ri-fare__slider"
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  step={1}
                  value={Math.min(sliderMax, Math.max(sliderMin, draft[cabin]))}
                  onChange={handleInput(cabin)}
                  disabled={disabled}
                  aria-label={`${CABIN_LABELS[cabin]} fare slider`}
                />
                <div className="ri-fare__number">
                  <span aria-hidden="true">$</span>
                  <input
                    id={inputId}
                    type="number"
                    min={1}
                    step={1}
                    inputMode="decimal"
                    value={Number.isFinite(draft[cabin]) ? draft[cabin] : ''}
                    onChange={handleInput(cabin)}
                    disabled={disabled}
                    aria-invalid={!disabled && !(draft[cabin] > 0)}
                  />
                </div>
              </div>

              {!disabled && (
                <div className={changed ? 'ri-fare__sensitivity ri-fare__sensitivity--changed' : 'ri-fare__sensitivity'}>
                  <PriceSensitivityIndicator sensitivity={sensitivity} />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <footer className="ri-panel__actions">
        <button type="button" className="ri-button ri-button--ghost" onClick={handleReset} disabled={!isDirty}>
          Reset
        </button>
        <button
          type="button"
          className="ri-button ri-button--primary"
          onClick={handleApply}
          disabled={!isDirty || !isValid}
        >
          Apply fares
        </button>
      </footer>
    </section>
  );
}
